import { PostHogConfig } from '@habity/types';
import { TrackingUserData } from '~/lib/tracking';
import posthog from 'posthog-js';
import { isDev } from '~/lib/utils';

// Singleton state
let isInitialized = false;
let initPromise: Promise<boolean> | null = null;

export function usePostHog() {
  // Show logs in development by default, unless explicitly disabled
  const showTrackingLogs =
    isDev && process.env.NEXT_PUBLIC_SHOW_TRACKING_LOGS !== 'false';

  // Initialize PostHog with API key and host
  const init = async (config: PostHogConfig) => {
    // Only run in browser environment
    if (typeof window === 'undefined') return false;

    // If already initialized, return early
    if (isInitialized) {
      return true;
    }

    // If initialization is in progress, wait for it to complete
    if (initPromise) {
      return await initPromise;
    }

    if (showTrackingLogs) {
      console.log('Initializing PostHog');
    }

    // Create a promise that will resolve when initialization is complete
    initPromise = new Promise((resolve) => {
      try {
        // Check if PostHog is already loaded on the page
        if (window.posthog) {
          if (showTrackingLogs) {
            console.log('PostHog already loaded on page');
          }
          isInitialized = true;
          resolve(true);
          return;
        }

        // Initialize PostHog with the provided config
        posthog.init(config.apiKey, {
          api_host: config.host || 'https://analytics.brkr.se',
          person_profiles: 'identified_only',
          defaults: '2025-05-24',
          loaded: () => {
            if (showTrackingLogs) {
              console.log('PostHog loaded successfully');
            }
            isInitialized = true;
            resolve(true);
          },
          capture_pageview: true,
          autocapture: true,
          persistence: 'localStorage+cookie',
          debug: showTrackingLogs,
        });
      } catch (error) {
        console.error('Error initializing PostHog:', error);
        initPromise = null;
        resolve(false);
      }
    });

    return await initPromise;
  };

  // Identify user in PostHog
  const identify = async (userId: number, userData: TrackingUserData) => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    // If initialization is in progress, wait for it to complete
    if (initPromise && !isInitialized) {
      try {
        await initPromise;
      } catch (error) {
        console.error('Error waiting for PostHog initialization:', error);
        return;
      }
    }

    // If still not initialized after waiting, return early
    if (!isInitialized) return;

    try {
      if (showTrackingLogs) {
        console.log('Identifying in PostHog', userId, userData);
      }

      // PostHog uses identify for setting user properties and id
      posthog.identify(userId.toString(), userData);
    } catch (error) {
      console.error('Error identifying user in PostHog:', error);
    }
  };

  // Track events in PostHog
  const track = async (
    eventName: string,
    properties?: Record<string, unknown>
  ) => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    // If initialization is in progress, wait for it to complete
    if (initPromise && !isInitialized) {
      try {
        await initPromise;
      } catch (error) {
        console.error('Error waiting for PostHog initialization:', error);
        return;
      }
    }

    // If still not initialized after waiting, return early
    if (!isInitialized) {
      if (showTrackingLogs) {
        console.warn('PostHog not initialized, skipping event:', eventName);
      }
      return;
    }

    try {
      if (showTrackingLogs) {
        console.log('Tracking in PostHog', eventName, properties);
      }

      // Ensure posthog is available
      if (window.posthog || posthog) {
        // Track the event in PostHog
        posthog.capture(eventName, properties);

        // Verify event was sent
        if (showTrackingLogs) {
          console.log('PostHog event sent:', eventName);
        }
      } else {
        console.error('PostHog not available for tracking:', eventName);
      }
    } catch (error) {
      console.error('Error tracking event in PostHog:', error);
    }
  };

  return {
    init,
    identify,
    track,
  };
}
